/**
 * RitualStreakCard - Displays ritual streak statistics
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Flame, Trophy, Calendar, Sparkles } from 'lucide-react';
import { useRitualStreak } from '@/hooks/useRitualData';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface RitualStreakCardProps {
  variant?: 'compact' | 'full';
  className?: string;
}

export const RitualStreakCard: React.FC<RitualStreakCardProps> = ({ 
  variant = 'full',
  className 
}) => {
  const { data: streakData, isLoading } = useRitualStreak();

  if (isLoading) {
    return (
      <Card className={className}>
        <CardContent className="p-4">
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  const streak = streakData?.currentStreak ?? 0;
  const longestStreak = streakData?.longestStreak ?? 0;
  const totalDays = streakData?.totalCompletedDays ?? 0;

  if (variant === 'compact') {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className={cn(
          "flex items-center gap-3 p-3 rounded-lg",
          "bg-gradient-to-r from-secondary/10 to-accent/10 border border-secondary/20",
          className
        )}
      >
        <motion.div
          animate={streak > 0 ? { scale: [1, 1.2, 1] } : {}}
          transition={{ duration: 0.5, repeat: streak > 2 ? Infinity : 0, repeatDelay: 3 }}
          className="p-2 rounded-full bg-secondary/20"
        >
          <Flame className="h-5 w-5 text-secondary" />
        </motion.div>
        
        <div className="flex-1">
          <p className="font-bold text-lg text-secondary">
            {streak} Day{streak !== 1 ? 's' : ''}
          </p>
          <p className="text-xs text-muted-foreground">
            {streak === 0 
              ? 'Start your healthy habits streak!' 
              : streak === 1 
                ? 'Great start!'
                : `${streak} days in a row!`
            }
          </p>
        </div>

        {longestStreak > streak && (
          <div className="text-right">
            <p className="text-xs text-muted-foreground">Best</p>
            <p className="font-semibold text-sm">{longestStreak}</p>
          </div>
        )}
      </motion.div>
    );
  }

  return (
    <Card className={className}>
      <CardContent className="p-4">
        <div className="grid grid-cols-3 gap-2 sm:gap-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-1 min-w-0"
          >
            <motion.div
              animate={streak > 0 ? { scale: [1, 1.15, 1] } : {}}
              transition={{ duration: 0.6, repeat: streak > 2 ? Infinity : 0, repeatDelay: 4 }}
              className="mx-auto p-2 rounded-full bg-secondary/20 w-fit"
            >
              <Flame className="h-4 w-4 sm:h-5 sm:w-5 text-secondary" />
            </motion.div>
            <p className="text-xl sm:text-2xl font-bold text-secondary">{streak}</p>
            <p className="text-[10px] sm:text-xs text-muted-foreground truncate">Current Streak</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="space-y-1 min-w-0"
          >
            <div className="mx-auto p-2 rounded-full bg-accent/20 w-fit">
              <Trophy className="h-4 w-4 sm:h-5 sm:w-5 text-accent" />
            </div>
            <p className="text-xl sm:text-2xl font-bold text-accent">{longestStreak}</p>
            <p className="text-[10px] sm:text-xs text-muted-foreground truncate">Best Streak</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-1 min-w-0"
          >
            <div className="mx-auto p-2 rounded-full bg-primary/20 w-fit">
              <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
            </div>
            <p className="text-xl sm:text-2xl font-bold text-primary">{totalDays}</p>
            <p className="text-[10px] sm:text-xs text-muted-foreground truncate">Total Days</p>
          </motion.div>
        </div>
      </CardContent>
    </Card>
  );
};
